import { useState } from 'react';

import { Icons } from '@/assets/icons/icons';
import { Button } from '@/components/ui/Button';
import { inputVariants } from '@/components/ui/Input';
import { XStack, YStack } from '@/components/ui/layout/layout';
import { Callout, FieldLabel } from '@/features/local-mcp';
import { XeroScopeChips } from '@/features/local-mcp/xero-scope-chips';
import {
  describeXeroProblem,
  xeroScopeList,
  type XeroCredentials,
} from '@/features/local-mcp/xero-setup';
import { useMcpServersQuery } from '@/features/settings/sub-settings/settings-local-mcp.query';

import { ConfigurationUnavailable } from './local-server-configuration';
import {
  useXeroConfiguration,
  type XeroStatus,
} from './use-xero-configuration';

import type { ConfigurationContext } from './configuration.types';
import type { ServerEntry } from '@littlebird/axon-mcp';

const CARD = 'rounded-lg border border-border bg-surface';
const INPUT_CLASS = inputVariants({ inputSize: 'sm' });

type SaveCredentials = (
  changes: Partial<XeroCredentials>,
  onSaved: () => void
) => void;

function StatusCard({
  status,
  onTestAgain,
}: {
  status: XeroStatus;
  onTestAgain: () => void;
}) {
  if (status.kind === 'checking') {
    return (
      <XStack className={`${CARD} items-center gap-2 p-3`}>
        <Icons.loader className="size-4 shrink-0 animate-spin text-foreground-muted" />
        <span className="text-sm text-foreground">Checking Xero…</span>
      </XStack>
    );
  }
  if (status.kind === 'connected') {
    return (
      <XStack className={`${CARD} items-center gap-2.5 px-3 py-2.5`}>
        <span className="flex size-[22px] shrink-0 items-center justify-center rounded-full bg-success-3 text-success-11">
          <Icons.check className="size-3.5" />
        </span>
        <span className="flex-1 text-sm text-foreground">
          Connected to {status.organisationName ?? 'Xero'}
        </span>
        <Button variant="ghost" size="sm" onClick={onTestAgain}>
          Test again
        </Button>
      </XStack>
    );
  }
  return (
    <Callout tone="danger" icon={<Icons.warn className="size-4" />}>
      <XStack className="items-start gap-3">
        <YStack className="min-w-0 flex-1 gap-0.5">
          <span className="text-sm font-medium text-foreground">
            Can’t connect to Xero
          </span>
          <span>{describeXeroProblem(status.problem, { inSetup: false })}</span>
        </YStack>
        <Button
          variant="ghost"
          size="sm"
          className="shrink-0"
          onClick={onTestAgain}
        >
          Test again
        </Button>
      </XStack>
    </Callout>
  );
}

function CredentialsSection({
  credentials,
  saving,
  onSave,
}: {
  credentials: XeroCredentials;
  saving: boolean;
  onSave: SaveCredentials;
}) {
  const [draft, setDraft] = useState<XeroCredentials | null>(null);

  if (draft === null) {
    return (
      <YStack className={`${CARD} gap-3 px-4 py-3`}>
        <YStack className="gap-0.5">
          <span className="text-xs text-foreground-muted">Client ID</span>
          <span className="break-all font-mono text-sm text-foreground">
            {credentials.clientId}
          </span>
        </YStack>
        <XStack className="items-end gap-3">
          <YStack className="flex-1 gap-0.5">
            <span className="text-xs text-foreground-muted">Client secret</span>
            <span className="font-mono text-sm text-foreground">
              {`•••• ${credentials.clientSecret.slice(-4)}`}
            </span>
          </YStack>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setDraft({ ...credentials, clientSecret: '' })}
          >
            Replace credentials
          </Button>
        </XStack>
      </YStack>
    );
  }

  const canSave =
    draft.clientId.trim() !== '' && draft.clientSecret.trim() !== '';
  return (
    <YStack className="gap-3">
      <YStack className="gap-1.5">
        <FieldLabel>Client ID</FieldLabel>
        <input
          aria-label="New client ID"
          value={draft.clientId}
          disabled={saving}
          onChange={(event) =>
            setDraft({ ...draft, clientId: event.target.value })
          }
          autoComplete="off"
          spellCheck={false}
          className={INPUT_CLASS}
        />
      </YStack>
      <YStack className="gap-1.5">
        <FieldLabel>Client secret</FieldLabel>
        <input
          type="password"
          aria-label="New client secret"
          value={draft.clientSecret}
          disabled={saving}
          onChange={(event) =>
            setDraft({ ...draft, clientSecret: event.target.value })
          }
          autoComplete="off"
          spellCheck={false}
          autoFocus
          className={INPUT_CLASS}
        />
      </YStack>
      <XStack className="justify-end gap-2">
        <Button
          variant="ghost"
          size="sm"
          disabled={saving}
          onClick={() => setDraft(null)}
        >
          Cancel
        </Button>
        <Button
          variant="default"
          size="sm"
          disabled={!canSave}
          isLoading={saving}
          aria-label="Save credentials"
          onClick={() =>
            onSave(
              { clientId: draft.clientId, clientSecret: draft.clientSecret },
              () => setDraft(null)
            )
          }
        >
          Save
        </Button>
      </XStack>
    </YStack>
  );
}

function ScopesSection({
  scopes,
  saving,
  onSave,
}: {
  scopes: string;
  saving: boolean;
  onSave: SaveCredentials;
}) {
  const [draft, setDraft] = useState<string | null>(null);

  return (
    <YStack className="gap-2">
      {draft === null ? (
        <XStack className="items-start gap-3">
          <div className="flex-1">
            <XeroScopeChips scopes={scopes} />
          </div>
          <Button variant="outline" size="sm" onClick={() => setDraft(scopes)}>
            Edit scopes
          </Button>
        </XStack>
      ) : (
        <XStack className="gap-2">
          <input
            aria-label="Scopes"
            value={draft}
            disabled={saving}
            onChange={(event) => setDraft(event.target.value)}
            spellCheck={false}
            autoFocus
            className={`${INPUT_CLASS} font-mono`}
          />
          <Button
            variant="ghost"
            size="sm"
            className="h-8"
            disabled={saving}
            onClick={() => setDraft(null)}
          >
            Cancel
          </Button>
          <Button
            variant="default"
            size="sm"
            className="h-8"
            disabled={xeroScopeList(draft).length === 0}
            isLoading={saving}
            aria-label="Save scopes"
            onClick={() => onSave({ scopes: draft }, () => setDraft(null))}
          >
            Save
          </Button>
        </XStack>
      )}
      <span className="text-xs text-foreground-muted">
        Must match the scopes turned on for the Custom Connection.
      </span>
    </YStack>
  );
}

function XeroSettings({ server }: { server: ServerEntry }) {
  const { credentials, status, saving, testAgain, saveCredentials } =
    useXeroConfiguration(server);

  return (
    <YStack className="gap-6 p-6">
      <StatusCard status={status} onTestAgain={testAgain} />
      <YStack className="gap-2">
        <FieldLabel>Custom Connection</FieldLabel>
        <CredentialsSection
          credentials={credentials}
          saving={saving}
          onSave={saveCredentials}
        />
      </YStack>
      <YStack className="gap-2">
        <FieldLabel>Scopes</FieldLabel>
        <ScopesSection
          scopes={credentials.scopes}
          saving={saving}
          onSave={saveCredentials}
        />
      </YStack>
    </YStack>
  );
}

export function XeroConfiguration({
  context,
}: {
  context: ConfigurationContext;
}) {
  const serverSlug =
    context.localSource?.kind === 'local_mcp'
      ? context.localSource.serverSlug
      : undefined;
  const serversQuery = useMcpServersQuery();
  const server = serversQuery.data?.find(
    (candidate) => candidate.slug === serverSlug
  );

  if (serversQuery.isPending) return null;
  if (serversQuery.isError || !server) {
    return (
      <ConfigurationUnavailable
        message="Couldn't read Xero from Littlebird's local settings."
        onRetry={() => void serversQuery.refetch()}
      />
    );
  }

  return <XeroSettings key={server.slug} server={server} />;
}
